import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { InventoryRow } from './InventoryRow';
import type { FilterByValueAction } from './InventoryRow';
import type { InventoryItem, InventorySortField, SortOrder } from '../../types/inventory';

interface InventoryTableProps {
    items: InventoryItem[];
    sortBy: InventorySortField;
    sortOrder: SortOrder;
    onSort: (field: InventorySortField) => void;
    canEdit: boolean;
    onCommentAdded: () => void;
    onFilterByValue?: (action: FilterByValueAction) => void;
    onShowTransactions?: (item: InventoryItem) => void;
    onToggleFlag?: (item: InventoryItem, flagType: 'ToBeDestroyed' | 'NewProduct') => void;
    onShowBuyers?: (item: InventoryItem) => void;
    onSendEmail?: (item: InventoryItem) => void;
    onShowEmails?: (item: InventoryItem) => void;
    onShowDetails?: (itemId: string) => void;
    compact?: boolean;
    selectedItemId?: string | null;
}

interface ColumnDef {
    key: InventorySortField | null;
    label: string;
    className?: string;
    essential?: boolean;
}

const COLUMNS: ColumnDef[] = [
    { key: null, label: '', className: 'w-8', essential: true },
    { key: 'companyCode', label: 'Co.' },
    { key: 'productName', label: 'Product', essential: true },
    { key: 'supplierName', label: 'Supplier' },
    { key: 'warehouseName', label: 'Warehouse' },
    { key: 'inventoryClass', label: 'Class', essential: true },
    { key: 'stockQuantity', label: 'Qty', className: 'text-right', essential: true },
    { key: 'stockValueEUR', label: 'Value EUR', className: 'text-right' },
    { key: 'pricePerKg', label: 'EUR/kg', className: 'text-right' },
    { key: 'oldestBatchDate', label: 'Oldest Batch' },
    { key: 'latestSalesDate', label: 'Last Sale' },
    { key: 'latestTransactionDate', label: 'Last Trans.' },
    { key: null, label: 'Customers' },
    { key: null, label: 'Orders' },
    { key: null, label: '', className: 'w-16', essential: true },
];

export function InventoryTable({
    items,
    sortBy,
    sortOrder,
    onSort,
    canEdit,
    onCommentAdded,
    onFilterByValue,
    onShowTransactions,
    onToggleFlag,
    onShowBuyers,
    onSendEmail,
    onShowEmails,
    onShowDetails,
    compact = false,
    selectedItemId = null,
}: InventoryTableProps) {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const toggleExpand = (itemId: string) => {
        setExpandedId(prev => prev === itemId ? null : itemId);
    };

    const SortIcon = ({ field }: { field: InventorySortField }) => {
        if (sortBy !== field) return <span className="ml-1 text-gray-300">&#8597;</span>;
        return sortOrder === 'ASC'
            ? <ChevronUp className="ml-0.5 inline h-3.5 w-3.5" />
            : <ChevronDown className="ml-0.5 inline h-3.5 w-3.5" />;
    };

    const visibleColumns = compact ? COLUMNS.filter(c => c.essential) : COLUMNS;

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {visibleColumns.map((col, i) => (
                            <th
                                key={i}
                                className={`px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 ${col.className || ''} ${col.key ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                                onClick={() => col.key && onSort(col.key)}
                            >
                                {col.label}
                                {col.key && <SortIcon field={col.key} />}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan={visibleColumns.length} className="px-3 py-12 text-center text-sm text-gray-500">
                                No inventory items found.
                            </td>
                        </tr>
                    ) : (
                        items.map(item => (
                            <InventoryRow
                                key={item.ItemID}
                                item={item}
                                isExpanded={!compact && expandedId === item.ItemID}
                                isSelected={selectedItemId === item.ItemID}
                                onToggle={() => toggleExpand(item.ItemID)}
                                canEdit={canEdit}
                                onCommentAdded={onCommentAdded}
                                colCount={visibleColumns.length}
                                onFilterByValue={onFilterByValue}
                                onShowTransactions={onShowTransactions}
                                onToggleFlag={onToggleFlag}
                                onShowBuyers={onShowBuyers}
                                onSendEmail={onSendEmail}
                                onShowEmails={onShowEmails}
                                onShowDetails={onShowDetails}
                                compact={compact}
                            />
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
